import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { NxtbnDrawer } from "..";
import useApi from "../../api";
import { TextBadge } from "../common";
import GenericSelectableTable from "../selectableTable";
import enumChoice from "../../enum";



const lineItemTableColumns = [
  { header: 'ID', key: 'order_line_item.id' },
  { header: 'Variant', key: 'order_line_item.name' },
  { header: 'Quantity', key: 'quantity' },
  { header: 'Reason', key: 'reason' },
  { header: 'Reason Details', key: 'reason_details' },
  { header: 'Receiving Status', key: 'receiving_status' },
  { header: 'Refunded Amount', key: 'refunded_amount' },
  { header: 'Destination', key: 'destination' },
];

function ReturnOrderDetails({ isOpen, onClose, id }: { isOpen: boolean; onClose: () => void; id: string }) {
  const api = useApi();
  const [returnOrderDetails, setReturnOrderDetails] = useState<any>(null);

  const bullkActionButtons = [
      {
        label: 'Received Selected',
        onClick: (selectedItems:any) => {
          const payload = {
            receiving_status: enumChoice.returnReceivingStatuses.toKV().RECEIVED.value,
            line_item_ids: Array.from(selectedItems),
          }
          api.updateReturnLineItemStatusBulk(payload).then((response) => {
            fetchReturnOrderDetails();
            toast.success("Return order status updated successfully");
          }, (error) => {
            toast.error("Failed to update return order status");
          })
        }
      },
      {
        label: 'Received with issue Selected',
        onClick: (selectedItems:any) => {
          const payload = {
            receiving_status: enumChoice.returnReceivingStatuses.toKV().RECEIVED_WITH_ISSUES.value,
            line_item_ids: Array.from(selectedItems),
          }
          api.updateReturnLineItemStatusBulk(payload).then((response) => {
            fetchReturnOrderDetails();
            toast.success("Return order status updated successfully");
          }, (error) => {
            toast.error("Failed to update return order status");
          })
        },
      },
      {
        label: 'Not Received Selected',
        onClick: (selectedItems:any) => {
          const payload = {
            receiving_status: enumChoice.returnReceivingStatuses.toKV().NOT_RECEIVED.value,
            line_item_ids: Array.from(selectedItems),
          }
          api.updateReturnLineItemStatusBulk(payload).then((response) => {
            fetchReturnOrderDetails();
            toast.success("Return order status updated successfully");
          }, (error) => {
            toast.error("Failed to update return order status");
          })
        }
      },
  ];


  const fetchReturnOrderDetails = () => {
    api.getReturnOrderDetails(id)
      .then((response) => {
        setReturnOrderDetails(response);
      })
      .catch(() => {
        toast.error("Failed to fetch return order details");
      });
  };

  const handleAction = (action: any) => {
    const payload = {
      status: action,
    }
    api.updateReturnRequestStatus(id, payload).then((response) => {
      fetchReturnOrderDetails();
      toast.success("Return order status updated successfully");
    }, (error) => {
      toast.error("Failed to update return order status");
    });
  }

  useEffect(() => {
    if (id) {
      fetchReturnOrderDetails();
    }
  }, [id]);

  if (!returnOrderDetails) return null;

  return (
    <NxtbnDrawer size='lg' title="Return Order Details" isOpen={isOpen} onClose={onClose}>
      {/* Action Buttons */}
      <div className="flex justify-end space-x-2 mt-4">
        <button
          onClick={() => handleAction(enumChoice.returnStatuses.toKV().REVIEWED.value)}
          className="px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-md transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
        >
          Mark as Reviewed
        </button>
        <button
          onClick={() => handleAction(enumChoice.returnStatuses.toKV().APPROVED.value)}
          className="px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-md transition-colors duration-200 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-offset-2"
        >
          Mark as Approved
        </button>
        <button
          onClick={() => handleAction(enumChoice.returnStatuses.toKV().COMPLETED.value)}
          className="px-3 py-1.5 bg-gray-500 text-white text-sm font-medium rounded-md transition-colors duration-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
        >
          Mark as Resolved
        </button>
        <button
          onClick={() => handleAction(enumChoice.returnStatuses.toKV().CANCELLED.value)}
          className="px-3 py-1.5 bg-red-500 text-white text-sm font-medium rounded-md transition-colors duration-200 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
        >
          Mark as Cancelled
        </button>
      </div>


      <div className="p-6 space-y-6">
        <h2 className="text-2xl font-bold text-gray-800">Return Ref ID: <span className="text-blue-600">RID{returnOrderDetails.id}</span></h2>
        <p>Order alias: <span>{returnOrderDetails.order_alias}</span></p>
        <div className="text-lg font-semibold text-gray-600">
          <p>Status: <TextBadge size="sm" value={returnOrderDetails.status} /></p>
          <p>Reason: {returnOrderDetails.reason}</p>
          <p>Reason Details: {returnOrderDetails.reason_details || "N/A"}</p>
        </div>

        <div className="text-gray-600">
          <p>Initiated By: {returnOrderDetails.initiated_by}</p>
          <p>Reviewed By: {returnOrderDetails.reviewed_by || "Not reviewed"}</p>
          <p>Approved By: {returnOrderDetails.approved_by || "Not approved"}</p>
          <p>Approved At: {returnOrderDetails.approved_at || "N/A"}</p>
          <p>Rejected At: {returnOrderDetails.rejected_at || "N/A"}</p>
          <p>Completed At: {returnOrderDetails.completed_at || "N/A"}</p>
          <p>Cancelled At: {returnOrderDetails.cancelled_at || "N/A"}</p>
        </div>

        {/* Line Items Table */}
        <div className="mt-4">
          <h3 className="text-xl font-bold text-gray-800">Line Items</h3>
          <GenericSelectableTable
            columns={lineItemTableColumns}
            data={returnOrderDetails.line_items}
            bullkActionButtons={bullkActionButtons}
          />
        </div>
      </div>
    </NxtbnDrawer>
  );
}

export default ReturnOrderDetails;
